/**
 * Picking a two-point sync's "Time in the file" off the cue table.
 *
 * The /sync-subtitles copy says: click a line in the cue table and its current time fills in,
 * then type when it should appear. Before this the page had no such thing, and every point had
 * to be read off the table and typed by hand.
 *
 * On that page the table shows the file AS DROPPED, not the result (`reference()` in
 * lib/tool-source.ts), so a row's start is the time in the file whatever the points already
 * do to the result. Without that, the second pick would read a time the first point had
 * already moved.
 *
 * WHICH BOX A PICK FILLS, in order:
 *
 *   1. The "Time in the file" box the user was last in, when the last option box they were in
 *      is one. A pick moves focus on to that point's "Time it should have", so the box stops
 *      being the last one at once: click, type, click fills the first point and then the
 *      second rather than the first twice.
 *   2. Otherwise the first EMPTY one: never filled in, or cleared. The box starts out showing
 *      its 0 default, and that 0 is not a time anyone gave it; a 0 the user typed is.
 *   3. Otherwise, with every point filled, the last one, which is the one a click most often
 *      means to correct.
 */

/** One point: the box that takes the time in the file, and the one beside it for the time it should have. */
export interface PointPair {
  readonly at: string;
  readonly to: string;
}

/**
 * The points `twoPointSync` publishes (src/transforms/timing/schema.ts), in the order the
 * page draws them.
 */
export const SYNC_POINTS: readonly PointPair[] = [
  { at: 'sync.aAt', to: 'sync.aTo' },
  { at: 'sync.bAt', to: 'sync.bTo' },
];

/**
 * The points a schema with these keys has. Empty on every page but /sync-subtitles, and an
 * empty answer is what keeps every other page's table read-only.
 */
export function pointPairs(keys: Iterable<string>): readonly PointPair[] {
  const have = new Set(keys);
  return SYNC_POINTS.filter((pair) => have.has(pair.at) && have.has(pair.to));
}

export interface PickState {
  /** True when the box under `key` holds a time someone gave it. */
  readonly filled: (key: string) => boolean;
  /** The key of the option box the user was last in, if any. */
  readonly lastFocused: string | null;
}

/** The point a pick fills, by the three rules above. `null` when the page has none. */
export function pickTarget(pairs: readonly PointPair[], state: PickState): PointPair | null {
  const focused = pairs.find((pair) => pair.at === state.lastFocused);
  if (focused !== undefined) return focused;
  return pairs.find((pair) => !state.filled(pair.at)) ?? pairs[pairs.length - 1] ?? null;
}
